import {useCallback} from 'react'

import {queries} from '../../../../common/react-query/queries'
import {hubQueryClient} from '../../../../common/react-query/QueryProvider'
import {useOrderRequestResultPolling} from '../../orderManagement/hooks/useOrderRequestResultPolling'
import {CancellationRequestStatus, CancellationResultType} from '../types/cancellationRequest.types'
import {resolveCancellationPollingResult} from '../utils/resolveCancellationPollingResult'

const CANCELLATION_POLL_INTERVAL_MS = 3000
const CANCELLATION_POLL_MAX_RETRIES = 10

export const useCancellationRequestPolling = (
  orderId: string,
  requestId?: string
): {
  triggerPolling: () => void
  isPolling: boolean
  result: CancellationResultType | undefined
} => {
  const getUpdatedStatus = useCallback(async (): Promise<CancellationResultType | undefined> => {
    if (!requestId) return undefined

    const request = await hubQueryClient.fetchQuery<CancellationRequestStatus | null>(
      ['cancelOrder', orderId, 'request', requestId],
      () => queries.cancellationRequestStatus(orderId),
      {retry: false, staleTime: 0}
    )
    if (request?.requestId === requestId) {
      hubQueryClient.setQueryData(['cancellationRequestStatus', orderId], request)
    }
    return resolveCancellationPollingResult(request, requestId)
  }, [orderId, requestId])

  const pollingInfo = useOrderRequestResultPolling(
    CANCELLATION_POLL_INTERVAL_MS,
    CANCELLATION_POLL_MAX_RETRIES,
    getUpdatedStatus
  )

  return {
    ...pollingInfo,
    result: pollingInfo.result === 'pendingDispatch' ? 'pending' : pollingInfo.result
  }
}
